import { ConnectProto } from "@racoon/api";
import { Namespace, TFn } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { StatusPill } from "@racoon/ui";

export { isShiftPlanned, isShiftRunning, isShiftTerminal } from "~/shared/lib/shift-status";

export const humanTruckType = (type: string | number | undefined, t: TFn) => {
  switch (type) {
    case "TRUCK_TYPE_CONSTRUCTION":
    case ConnectProto.TruckType.CONSTRUCTION:
      return t(Namespace.Autopark, "types.construction");
    case "TRUCK_TYPE_DOUBLE_CONTAINER":
    case ConnectProto.TruckType.DOUBLE_CONTAINER:
      return t(Namespace.Autopark, "types.double_container");
    case "TRUCK_TYPE_MSW":
    case ConnectProto.TruckType.MSW:
      return t(Namespace.Autopark, "types.msw");
    case "TRUCK_TYPE_TIPPER":
    case ConnectProto.TruckType.TIPPER:
      return t(Namespace.Autopark, "types.tipper");
    case "TRUCK_TYPE_LIQUID_WASTE":
    case ConnectProto.TruckType.LIQUID_WASTE:
      return t(Namespace.Autopark, "types.liquid");
    case "TRUCK_TYPE_LOADER_CRANE":
    case ConnectProto.TruckType.LOADER_CRANE:
      return t(Namespace.Autopark, "types.loader_crane");
    default:
      return t(Namespace.Autopark, "types.other");
  }
};

/** Status colours, shared by the shift badge and the truck card's tab dots. */
export const shiftStatusTone = (status: ConnectProto.DriverOnShiftStatus) => {
  switch (status) {
    case ConnectProto.DriverOnShiftStatus.ENDED:
      return { badge: "bg-icon-secondary text-white", dot: "bg-icon-secondary" };
    case ConnectProto.DriverOnShiftStatus.CANCELLED:
      return { badge: "bg-brand-red-light text-brand-red", dot: "bg-brand-red" };
    case ConnectProto.DriverOnShiftStatus.NOT_APPROVED:
      return { badge: "bg-surface-assigned text-text-information", dot: "bg-brand-blue" };
    default:
      return { badge: "bg-icon-success text-white", dot: "bg-icon-success" };
  }
};

/**
 * Label for a shift status, shared by the badge and the xlsx export. An ended
 * shift with an approved TTN (`isTtnApproved`) keeps the ENDED tone but says so.
 */
export const shiftStatusLabel = (
  status: ConnectProto.DriverOnShiftStatus,
  t: TFn,
  ttnApproved = false,
) => {
  switch (status) {
    case ConnectProto.DriverOnShiftStatus.ENDED:
      return ttnApproved
        ? t(Namespace.Autopark, "shifts.status.ttnApproved")
        : t(Namespace.Autopark, "shifts.status.completed");
    case ConnectProto.DriverOnShiftStatus.CANCELLED:
      return t(Namespace.Autopark, "shifts.status.cancelled");
    case ConnectProto.DriverOnShiftStatus.NOT_APPROVED:
      return t(Namespace.Autopark, "shifts.status.notApproved");
    default:
      return t(Namespace.Autopark, "shifts.status.active");
  }
};

const MUTED_TONE = "border-text-secondary/40 text-text-secondary bg-slate-50";

/**
 * The card's header chip. Archived and in-repair come from `truck.status` alone,
 * but a working truck reads as «На линии» only while it carries a shift that has
 * not ended — otherwise it is sitting in the yard. Per-shift state stays in the
 * tabs, on ShiftStatusBadge.
 */
export const humanTruckBadge = (
  status: ConnectProto.TruckStatus | undefined,
  hasActiveShift: boolean,
  t: TFn,
): React.ReactNode => {
  const { label, tone } =
    status === ConnectProto.TruckStatus.INACTIVE
      ? { label: t(Namespace.Autopark, "truckStatus.archived"), tone: MUTED_TONE }
      : status === ConnectProto.TruckStatus.IN_REPAIR
        ? {
            label: t(Namespace.Autopark, "truckStatus.repair"),
            tone: "border-accent-yellow text-accent-yellow bg-brand-yellow-light",
          }
        : status === ConnectProto.TruckStatus.ACTIVE && hasActiveShift
          ? {
              label: t(Namespace.Autopark, "truckStatus.onLine"),
              tone: "border-brand-green text-brand-green bg-brand-green/10",
            }
          : { label: t(Namespace.Autopark, "truckStatus.inYard"), tone: MUTED_TONE };

  return (
    <StatusPill
      data-testid="truck-card-badge"
      radius="lg"
      density="tight"
      tone={tone}
      className="h-6 border !px-2"
    >
      {label}
    </StatusPill>
  );
};

/** Placeholder for a figure the backend has no data for — never a fabricated 0. */
export const NO_DATA = "-";

/** Distance the fuel figure is quoted over, in km: the column reads l/100 km. */
const LITRES_PER_DISTANCE = 100;

/**
 * Undefined whenever the ratio cannot be known — either figure missing, or fuel
 * burned without moving — so the cell can render a dash instead of an Infinity.
 */
export const fuelPerDistance = (
  consumedLitres: number | undefined,
  mileageKm: number | undefined,
): number | undefined => {
  if (consumedLitres == null || mileageKm == null || mileageKm === 0) return undefined;
  return (consumedLitres / mileageKm) * LITRES_PER_DISTANCE;
};

/**
 * A telemetry figure with its unit (`123 км`, `+150 л`). The unit stays put when
 * the figure is missing (`- км`), so a column of dashes still reads as the
 * quantity it is about.
 */
export const formatUsage = (
  value: number | undefined,
  unit: string,
  { sign = "", fractionDigits = 0 }: { sign?: string; fractionDigits?: number } = {},
) => {
  if (value == null) return `${NO_DATA} ${unit}`;
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${sign}${formatted} ${unit}`;
};

type TimestampLike = { seconds?: string | number | bigint; nanos?: number };

/**
 * Date a shift counts as "released" (the waybill's date, the "Выпуск" column).
 * Once the shift has started, that moment is what the document is about;
 * `twStartAt` (the planned window) stands in until then, and `createdAt` only
 * when the backend sent neither — so the precedence is
 * `startedAt` → `twStartAt` → `createdAt`.
 */
export const resolveShiftReleasedAt = (shift: {
  startedAt?: TimestampLike;
  twStartAt?: TimestampLike;
  createdAt?: TimestampLike;
}): string =>
  (
    fromTimestamp(shift.startedAt) ??
    fromTimestamp(shift.twStartAt) ??
    fromTimestamp(shift.createdAt)
  )?.toISOString() ?? new Date().toISOString();
